import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Grid3X3 } from 'lucide-react';
import { getCurrencySymbol } from '../../lib/currency';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { RoleMarginTable } from './RoleMarginTable';
import { RoleWhatIfPanel } from './RoleWhatIfPanel';
import { simulateMatrix, pct, aud, delta } from './roleMarginMath';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Leadership: role × component-group margin matrix with a rate what-if (simulate + apply). */
export function RoleMarginMatrix({ estimateId, compact = false, onApplied }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sellRates, setSellRates] = useState({});
  const [costRates, setCostRates] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  const load = () => axios.get(`${API_URL}/api/estimates/${estimateId}/role-margin`, { withCredentials: true })
    .then((r) => { setData(r.data); setSellRates({}); setCostRates(baseCosts(r.data)); })
    .catch((e) => setError(e?.response?.data?.detail || 'Could not load role margin'));
  useEffect(() => { if (estimateId) { setData(null); setError(null); load(); } }, [estimateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = useMemo(() => (data?.configured ? simulateMatrix(data, {}, baseCosts(data)) : null), [data]);
  const sim = useMemo(() => (data?.configured ? simulateMatrix(data, numeric(sellRates), costRates) : null), [data, sellRates, costRates]);
  const sellOnly = useMemo(() => (data?.configured ? simulateMatrix(data, numeric(sellRates), baseCosts(data)) : null), [data, sellRates]);

  if (error) return <p className="text-xs text-rose-600" data-testid="role-margin-error">{error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-xs text-slate-400 py-3" data-testid="role-margin-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading role margin…</div>;
  if (!data.configured) return <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="role-margin-not-configured">Add internal cost rates in Admin → Settings to see margin by role and component group.</p>;
  if (!data.roles?.length) return <p className="text-xs text-slate-400" data-testid="role-margin-empty">No component breakdown on this estimate yet.</p>;

  const sym = getCurrencySymbol(data.currency_code);
  const changedSell = Object.fromEntries(Object.entries(numeric(sellRates)).filter(([r, v]) => Math.abs(v - data.role_totals[r].sell_rate_native) > 0.005));
  const dirtySell = Object.keys(changedSell).length > 0;
  const dirty = dirtySell || Object.keys(costRates).some((r) => String(costRates[r] ?? '') !== String(data.role_totals[r]?.cost_rate_aud ?? ''));
  const dOverall = delta(sim.overall.margin_pct, base.overall.margin_pct);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(`${API_URL}/api/estimates/${estimateId}/role-margin/apply`, { sell_rates: changedSell }, { withCredentials: true });
      toast.success(`Rates applied — saved as v${r.data.version_number}`);
      setData(r.data); setSellRates({}); setCostRates(baseCosts(r.data)); setConfirm(false);
      onApplied?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not apply rates'); } finally { setApplying(false); }
  };

  return (
    <div className="space-y-3" data-testid="role-margin-matrix">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Grid3X3 className="w-3.5 h-3.5 text-slate-400" />
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Margin by role × component group <span className="font-normal normal-case">· A$ · sell − internal cost</span></p>
        </div>
        <div className="text-xs tabular-nums" data-testid="role-margin-overall-kpi">
          <span className="text-slate-500">Overall margin </span>
          <span className="font-semibold text-[#0F2040]">{pct(base.overall.margin_pct)}</span>
          {dirty && dOverall != null && Math.abs(dOverall) >= 0.05 && (
            <span className={`ml-1 font-semibold ${dOverall > 0 ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="role-margin-overall-delta">→ {pct(sim.overall.margin_pct)} ({dOverall > 0 ? '+' : '−'}{Math.abs(dOverall).toFixed(1)} pts · {delta(sim.overall.margin_aud, base.overall.margin_aud) >= 0 ? '+' : '−'}{aud(Math.abs(delta(sim.overall.margin_aud, base.overall.margin_aud)))})</span>
          )}
        </div>
      </div>
      <RoleMarginTable data={data} sim={sim} base={dirty ? base : null} min={data.min_margin_pct} compact={compact} />
      {data.roles_missing_cost_rate?.length > 0 && (
        <p className="text-[10px] text-amber-700" data-testid="role-margin-missing-roles">* No internal cost rate for {data.roles_missing_cost_rate.join(', ')} — their cost counts as A$0 in group and overall margin (same as the headline margin). Type a cost rate below to simulate one.</p>
      )}
      {data.locked && <p className="text-[10px] text-slate-500" data-testid="role-margin-locked">Effort &amp; cost are locked on this estimate — what-if is simulation only.</p>}
      <RoleWhatIfPanel data={data} sellRates={sellRates} costRates={costRates} sim={sim} sym={sym}
        onSell={(r, v) => setSellRates((s) => ({ ...s, [r]: v }))}
        onCost={(r, v) => setCostRates((s) => ({ ...s, [r]: v }))}
        onReset={() => { setSellRates({}); setCostRates(baseCosts(data)); }}
        onApply={() => setConfirm(true)} dirtySell={dirtySell} dirty={dirty} applying={applying} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="role-whatif-apply-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply sell rates to this estimate?</AlertDialogTitle>
            <AlertDialogDescription>
              {Object.entries(changedSell).map(([r, v]) => `${r}: ${sym}${data.role_totals[r].sell_rate_native.toFixed(0)} → ${sym}${Number(v).toFixed(0)}/h`).join(' · ')}.
              {' '}This sets a per-role rate card override and re-prices the estimate at the same hours, saved as a new version (overall margin {pct(base.overall.margin_pct)} → {pct(sellOnly.overall.margin_pct)}). Cost-rate what-ifs are not saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-whatif-apply-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); apply(); }} disabled={applying} className="bg-[#0F2040] hover:bg-[#1a2f57]" data-testid="role-whatif-apply-confirm">{applying ? 'Applying…' : 'Apply & save version'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const baseCosts = (d) => Object.fromEntries((d.roles || []).map((r) => [r, d.role_totals[r]?.cost_rate_aud ?? '']));
const numeric = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v != null && !Number.isNaN(Number(v))).map(([k, v]) => [k, Number(v)]));
